import { PUBLIC_LOCALES } from '@/lib/locale-prefix'
import { VIDEO_DELIVERY_SEGMENT_PATTERN } from '@/lib/santa-video/delivery-route'
import { VIDEO_SHOP_PATH } from '@/lib/santa-video/shop-routes'

const SENSITIVE_AUTH_URL_KEYS = new Set([
  'access_token',
  'refresh_token',
  'provider_token',
  'provider_refresh_token',
  'id_token',
  'code',
  'token',
  'authorization',
])

/**
 * Las URLs se clasifican en tres clases (`classifyAnalyticsUrl`) y cada
 * consumidor decide cuáles bloquea:
 *
 * - `credential`: lleva una credencial de autenticación (callback de OAuth,
 *   códigos de un solo uso, tokens en query o hash). No arranca NINGUNA
 *   analítica, PostHog incluido: el SDK lee la URL al iniciar, antes de
 *   cualquier filtro.
 * - `capability`: el propio CAMINO es la credencial. Hoy sólo
 *   `/tu-video/<publicToken>`: quien la tiene ve y descarga el vídeo sin sesión.
 *   GTM y `@vercel/analytics` no cargan (publicarían la URL entera). PostHog sí
 *   (ADR-054: la entrega se comparte y trae tráfico), pero nunca ve el token:
 *   `maskVideoDeliveryToken` lo sustituye antes del envío y el replay no graba
 *   estas rutas (`lib/session-recording-route-guard.ts`).
 * - `public`: todo lo demás.
 *
 * El prefijo de idioma es OPCIONAL en la expresión a propósito: next-intl sirve
 * la misma página en `/tu-video/…` y en `/en/tu-video/…`, así que las siete
 * formas son rutas vivas y las siete tienen que caer aquí. La alternancia se
 * CONSTRUYE desde `PUBLIC_LOCALES`: un `[a-z]{2}` a ojo dejaría de acertar en
 * silencio el día que entre un `pt-BR`. `es` va en la lista aunque no lleve
 * prefijo (`localePrefix: 'as-needed'`): cubrirlo no cuesta nada.
 */
const LOCALE_PREFIXES = PUBLIC_LOCALES.join('|')
const VIDEO_DELIVERY_PATH = new RegExp(`^/(?:(?:${LOCALE_PREFIXES})/)?${VIDEO_DELIVERY_SEGMENT_PATTERN}/`, 'i')

export type AnalyticsUrlClass = 'credential' | 'capability' | 'public'

/** Lo que analítica ve en lugar del token: el segmento queda, el valor no. */
export const VIDEO_DELIVERY_TOKEN_PLACEHOLDER = ':token'

/**
 * El token dentro de CUALQUIER texto (URL absoluta, relativa, referrer, título
 * de enlace), no sólo de un `pathname`: PostHog copia la URL en una docena de
 * propiedades (`$current_url`, `$pathname`, `$referrer`, `$prev_pageview_*`,
 * `$initial_*` dentro de `$set_once`…) y una lista de claves se quedaría corta
 * el día que la librería añada otra.
 */
const VIDEO_DELIVERY_TOKEN_IN_TEXT = new RegExp(`(/${VIDEO_DELIVERY_SEGMENT_PATTERN}/)[^/?#\\s"'<>]+`, 'gi')

/** Sustituye el token de la entrega por `VIDEO_DELIVERY_TOKEN_PLACEHOLDER`. Idempotente. */
export function maskVideoDeliveryToken(value: string): string {
  return value.replace(VIDEO_DELIVERY_TOKEN_IN_TEXT, `$1${VIDEO_DELIVERY_TOKEN_PLACEHOLDER}`)
}

/** La identidad que un tag de terceros ve en lugar de la URL real. */
export interface MaskedAnalyticsPage {
  /** URL absoluta y neutra; nunca contiene la ruta sensible. */
  location: string
  title: string
}

/**
 * Rutas-capacidad que, aun así, tienen que medir algo en Google (la conversión
 * de compra de la entrega, que es el `success_url` de Stripe). No se publican:
 * se SUSTITUYEN por una página neutra, y `lib/google-analytics` aplica la
 * sustitución en cada punto que envía ubicación (config, script, conversión).
 * Una ruta-capacidad que no esté aquí no puede mandar nada a Google.
 */
const MASKED_PAGES = [
  { route: VIDEO_DELIVERY_PATH, path: VIDEO_SHOP_PATH, title: 'Vídeo Papá Noel' },
] as const

type AnalyticsLocation = Pick<Location, 'pathname' | 'search' | 'hash'>

function currentLocationOr<T extends Partial<Location>>(location?: T): T | Location | undefined {
  return location ?? (typeof window !== 'undefined' ? window.location : undefined)
}

function hasSensitiveParams(params: URLSearchParams): boolean {
  return Array.from(params.keys()).some(isSensitiveAuthKey)
}

export function isSensitiveAuthKey(key: string): boolean {
  return SENSITIVE_AUTH_URL_KEYS.has(key.toLowerCase())
}

function hasAuthCredential({ pathname, search, hash }: AnalyticsLocation): boolean {
  if ((pathname ?? '').replace(/\/+$/, '') === '/auth/callback') return true
  if (hasSensitiveParams(new URLSearchParams(search ?? ''))) return true

  const fragment = (hash ?? '').replace(/^#/, '')
  if (!fragment) return false
  const hashParams = fragment.includes('?') ? fragment.slice(fragment.indexOf('?') + 1) : fragment
  return hasSensitiveParams(new URLSearchParams(hashParams))
}

/**
 * La clase de la URL (por defecto, la del navegador). Sin `window` ni
 * `location` es `public`: en el servidor no hay URL que filtrar.
 */
export function classifyAnalyticsUrl(location?: AnalyticsLocation): AnalyticsUrlClass {
  const current = currentLocationOr(location)
  if (!current) return 'public'
  if (hasAuthCredential(current)) return 'credential'
  if (VIDEO_DELIVERY_PATH.test(current.pathname ?? '')) return 'capability'
  return 'public'
}

/**
 * La página neutra que sustituye a la actual, o `null` si la actual no se
 * enmascara (porque no es sensible, o porque no debe medirse en absoluto).
 */
export function maskedAnalyticsPage(
  location?: Pick<Location, 'pathname' | 'origin'>
): MaskedAnalyticsPage | null {
  const currentLocation = currentLocationOr(location)
  const pathname = currentLocation?.pathname
  if (!currentLocation || !pathname) return null

  const masked = MASKED_PAGES.find(({ route }) => route.test(pathname))
  if (!masked) return null
  return { location: new URL(masked.path, currentLocation.origin).toString(), title: masked.title }
}

/**
 * Removes OAuth material from a URL without changing non-sensitive query
 * parameters, and masks the video delivery token (`maskVideoDeliveryToken`).
 */
export function sanitizeAnalyticsUrl(value: string): string {
  const isAbsoluteUrl = /^[a-z][a-z\d+.-]*:\/\//i.test(value)
  const isRelativeUrl = value.startsWith('/')
  if (!isAbsoluteUrl && !isRelativeUrl) return value

  try {
    const url = new URL(value, 'https://analytics.invalid')
    for (const key of Array.from(url.searchParams.keys())) {
      if (isSensitiveAuthKey(key)) url.searchParams.delete(key)
    }
    url.hash = ''

    return maskVideoDeliveryToken(isAbsoluteUrl ? url.toString() : `${url.pathname}${url.search}`)
  } catch {
    return maskVideoDeliveryToken(value.split('#', 1)[0])
  }
}
